import { StorageError } from '../errors.js'

/**
 * Numbered, checkpointed, idempotent migrations — LOC-10.
 * Each migration runs, then its version is checkpointed. An interruption leaves
 * the store at the last completed version; rerunning re-applies only pending
 * migrations. Migrations must therefore be idempotent and interruption-safe.
 */

export interface MigrationStateStore {
  readVersion(): Promise<number>
  writeVersion(version: number): Promise<void>
}

export interface VaultMigration {
  readonly version: number
  readonly description: string
  run(state: MigrationStateStore): Promise<void>
}

export interface MigrationReport {
  readonly from_version: number
  readonly to_version: number
  readonly applied: readonly number[]
  readonly current_version: number
}

function fail(message: string, cause: unknown): StorageError {
  const detail = cause instanceof Error ? cause.message : String(cause)
  return new StorageError('OP_STORAGE_MIGRATION_FAILED', `${message}: ${detail}`)
}

export async function runMigrations(
  store: MigrationStateStore,
  migrations: readonly VaultMigration[],
): Promise<MigrationReport> {
  const versions = new Set<number>()
  for (const migration of migrations) {
    if (!Number.isInteger(migration.version) || migration.version < 1) {
      throw fail(`migration version ${migration.version} must be a positive integer`, 'invalid')
    }
    if (versions.has(migration.version)) {
      throw fail(`duplicate migration version ${migration.version}`, 'invalid')
    }
    versions.add(migration.version)
  }

  const from = await store.readVersion()
  if (!Number.isInteger(from) || from < 0) {
    throw fail(`stored schema version ${from} is invalid`, 'invalid')
  }

  const pending = [...migrations]
    .sort((a, b) => a.version - b.version)
    .filter((migration) => migration.version > from)

  const applied: number[] = []
  for (const migration of pending) {
    try {
      await migration.run(store)
    } catch (error) {
      throw fail(`migration ${migration.version} (${migration.description}) failed`, error)
    }
    try {
      await store.writeVersion(migration.version)
    } catch (error) {
      throw fail(`checkpoint for migration ${migration.version} failed`, error)
    }
    applied.push(migration.version)
  }

  const highest = migrations.reduce((max, migration) => Math.max(max, migration.version), from)
  return {
    from_version: from,
    to_version: highest,
    applied,
    current_version: await store.readVersion(),
  }
}

/** Data migrations for the vault; schema-shape changes live in the IDB upgrade. */
export const VAULT_DATA_MIGRATIONS: readonly VaultMigration[] = []
